import { API } from "../helpers/API/amplifyApi";
import dayjsTZ from "../utils/dayjs";
import { requestDataWithParams } from "../helpers/API/RequestFactory";
import axios from "axios";
import { message } from "antd";
import { buildClockingEmployeeSnapshot } from "../utils/clockingEmployeeSnapshot";

const LEAVE_REQUEST_TYPES = new Set([
  "Pushime",
  "Leje Ditore",
  "Dite Personale",
  "Sëmurë",
  "Leje Lindje",
]);

function isWeekend(date = new Date()) {
  return date.getDay() === 6 || date.getDay() === 0;
}

async function fetchTodaysHolidayRequestClockings() {
  const limiter = dayjsTZ().startOf("day").valueOf();
  const endLimiter = dayjsTZ().endOf("day").valueOf();
  const clockings = await API.get("clocking", "/clocking", {
    queryStringParameters: { limiter, endLimiter },
  }).catch(() => []);
  if (!Array.isArray(clockings)) return [];
  return clockings.filter((c) => c?.dayType === "HolidayRequest");
}

function hasHolidayRequestForEmployee(existing, employeeId) {
  if (!employeeId || !Array.isArray(existing)) return false;
  const dayKey = dayjsTZ().format("DD/MM/YYYY");
  return existing.some((c) => {
    if (c?.employeeId !== employeeId) return false;
    if (c?.dayType !== "HolidayRequest") return false;
    return dayjsTZ(c?.clockInDate).format("DD/MM/YYYY") === dayKey;
  });
}

export const automateRequestsToApprovals = async (userData, ipResponse) => {
  if (isWeekend()) {
    return Promise.reject("Weekend");
  }

  const employees = await API.get("employees", "/employees");
  const applicableShifts = await API.get("shifts", "/shifts");

  const requests = await requestDataWithParams(
    "employeeRequests",
    dayjsTZ().subtract(1, "years").valueOf()
  ).then((res) => {
    if (!res || !Array.isArray(res)) {
      return [];
    }

    return res
      .filter(isApprovedRequest)
      .filter(isLeaveRequestType)
      .filter(isRequestForToday)
      .filter(isAfter10AMAndNotProcessedToday)
      .map((request) =>
        populateWithEmployeeData(request, employees, applicableShifts)
      )
      .filter(hasActiveEmployee);
  });

  if (requests?.length === 0) {
    return Promise.reject("No requests to convert");
  }

  const existingHolidayShifts = await fetchTodaysHolidayRequestClockings();
  const createdForEmployee = new Set(
    existingHolidayShifts.map((c) => c?.employeeId).filter(Boolean)
  );

  for (const employeeReq of requests) {
    const employeeId =
      employeeReq?.employeeId || employeeReq?.employee?.employeeId;
    if (!employeeId) continue;
    if (
      createdForEmployee.has(employeeId) ||
      hasHolidayRequestForEmployee(existingHolidayShifts, employeeId)
    ) {
      await markCreated(employeeReq);
      continue;
    }

    // Claim before create so concurrent tabs skip this request
    await markCreated(employeeReq);
    createdForEmployee.add(employeeId);

    try {
      await createNewShift(employeeReq, userData, ipResponse);
    } catch (err) {
      console.error("Failed to auto-create HolidayRequest shift", err);
    }
  }

  return true;
};

export const createNewShift = async (emplReq, userData, ipResponse) => {
  const employeeId = emplReq?.employeeId || emplReq?.employee?.employeeId;
  if (!employeeId) {
    message.error("Mungon ID e punonjesit");
    return Promise.reject("Mungon ID e punonjesit");
  }

  const res = await axios.get("https://api.ipify.org/?format=json");
  const tempResp = ipResponse || {};
  tempResp.IPv4 = res?.data?.ip || "";

  let startShift = !!emplReq?.shift?.shiftStartTime
    ? dayjsTZ(emplReq?.shift?.shiftStartTime)
    : dayjsTZ().set("hour", 13).set("minute", 30);
  let endShift = !!emplReq?.shift?.shiftEndTime
    ? dayjsTZ(emplReq?.shift?.shiftEndTime)
    : dayjsTZ().set("hour", 21).set("minute", 30);

  const [s_hour, s_minute] = [startShift.hour(), startShift.minute()];
  const [e_hour, e_minute] = [endShift.hour(), endShift.minute()];

  const employeeSnapshot = buildClockingEmployeeSnapshot(
    emplReq?.employee || null
  );

  const toSend = {
    clockInDate: dayjsTZ()
      .set("hour", s_hour)
      .set("minute", s_minute)
      .valueOf(),
    clockOutDate: dayjsTZ()
      .set("hour", e_hour)
      .set("minute", e_minute)
      .valueOf(),
    employeeId,
    ...employeeSnapshot,
    clockingCategory: "manualAdmin",
    dayType: "HolidayRequest",
    clockInNote: "",
    clockOutNote: "",
    editedClockIn: false,
    editedClockOut: false,
    editedByAdmin: true,
    forgotClockOut: false,
    approved: false,
    clockingLogs: [
      {
        createdAt: dayjsTZ().valueOf(),
        note: "Krijuar në mënyrë automatike",
        newShiftByAdmin: true,
        username:
          userData?.user?.given_name + " " + userData?.user?.family_name,
        userSub: userData?.user?.userSub,
        ipResponse: tempResp || null,
      },
    ],
  };
  return await API.post("clocking", "/clocking", {
    body: {
      ...toSend,
    },
  });
};

export const markCreated = async (emplReq) => {
  return await API.put(
    "employeeRequests",
    `/employeeRequests/${emplReq?.requestId}`,
    {
      body: {
        lastCreated: dayjsTZ().format("DD/MM/YYYY"),
      },
    }
  );
};

const isApprovedRequest = (request) => {
  return request?.requestStatus === "Aprovuar";
};

const isLeaveRequestType = (request) => {
  if (!request?.requestType) return true;
  return LEAVE_REQUEST_TYPES.has(request.requestType);
};

const isRequestForToday = (request) => {
  if (!request?.requestPeriod || !Array.isArray(request.requestPeriod)) {
    return false;
  }

  const todayFormatted = dayjsTZ().format("DD/MM/YYYY");
  return request.requestPeriod.some(
    (periodDate) => dayjsTZ(periodDate).format("DD/MM/YYYY") === todayFormatted
  );
};

const isAfter10AMAndNotProcessedToday = (request) => {
  const time10AM = dayjsTZ().set("hour", 10).set("minute", 0);
  const isAfter10AM = dayjsTZ().isAfter(time10AM);

  if (!isAfter10AM) {
    return false;
  }

  const lastCreated = request?.lastCreated;
  const todayFormatted = dayjsTZ().format("DD/MM/YYYY");

  if (!lastCreated) {
    return true;
  }

  if (lastCreated === todayFormatted) {
    return false;
  }

  return true;
};

const populateWithEmployeeData = (request, employees, applicableShifts) => {
  if (!employees || !Array.isArray(employees)) {
    return { ...request, employee: null, shift: null, employeeShift: null };
  }

  const selectedEmployee = employees.find(
    (employee) => employee?.userSub === request?.userSub
  );

  if (!selectedEmployee) {
    return { ...request, employee: null, shift: null, employeeShift: null };
  }

  const employeeShift = selectedEmployee?.employeeShiftId?.[0] || null;
  const shift =
    applicableShifts?.find((s) => s?.shiftId === employeeShift) || null;

  return {
    ...request,
    shift,
    employee: selectedEmployee,
    employeeShift,
  };
};

const hasActiveEmployee = (enrichedRequest) => {
  return enrichedRequest?.employee?.employeeStatus === "Aktiv";
};
